import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import FiIcons from '../fiIconMap';
import LayoutDefault from '../layouts/LayoutDefault';
import RouteSeo from '../../../seo/RouteSeo';
import { MARKETING_PAGE_META } from '../../../data/marketingPageMeta';
import '../assets/scss/style.scss';

const MESSAGE_YOURSELF_CARDS = [
    {
        path: '/message-yourself/whatsapp',
        title: 'WhatsApp Message Yourself',
        description:
            'Keep the quick self-chat instinct, but give links, screenshots, voice notes and reminders a personal inbox built for retrieval.'
    },
    {
        path: '/message-yourself/telegram',
        title: 'Telegram Saved Messages',
        description:
            'Saved Messages is strong. Luckynote adds a separate home for personal capture, with image text search, voice search and follow-up.'
    },
    {
        path: '/message-yourself/signal',
        title: 'Signal Note to Self',
        description:
            'Use Signal when end-to-end encrypted messaging matters. Use Luckynote for personal material you want searchable and actionable.'
    },
    {
        path: '/message-yourself/slack',
        title: 'Slack DM yourself',
        description:
            'A self-DM is handy at work. Luckynote keeps personal notes, files and research outside workspace access and retention rules.'
    },
    {
        path: '/message-yourself/discord',
        title: 'Discord private server notes',
        description:
            'A private server is clever, but it adds setup. Luckynote keeps the quick capture habit without server-shaped overhead.'
    }
];

const MessageYourselfPage = () => {
    const pages = MESSAGE_YOURSELF_CARDS.map((card) => {
        const page = MARKETING_PAGE_META.find((candidate) => candidate.path === card.path);
        return {
            path: card.path,
            title: card.title,
            description: card.description,
            icon: page?.icon,
            color: page?.iconColor,
            background: page?.iconBackground
        };
    }).filter((page) => page.icon);

    return (
        <>
            <RouteSeo
                title="Message Yourself, Then Find It Again | Luckynote"
                description="You already text yourself notes, links, files and screenshots. Luckynote is the personal inbox built to make them searchable and useful later."
                canonical="https://luckynote.io/message-yourself"
            />
            <LayoutDefault>
                <DarkSection>
                    <PageShell>
                        <HeroSection>
                            <HeroTitle>The note-to-self habit, wherever you picked it up.</HeroTitle>
                            <HeroDescription>
                                Luckynote turns the things you message yourself into a personal inbox you can search,
                                remember and act on.
                            </HeroDescription>
                            <IntroText>
                                Maybe it started with a WhatsApp chat, Telegram Saved Messages, Signal Note to Self,
                                a Slack DM, or a private Discord server. The instinct is the same: send it somewhere
                                before it disappears. Luckynote is a home designed around it, with one chat-style
                                inbox for links, screenshots, photos, voice notes, files, notes and tasks, plus
                                plain-language search, reminders, stars, optional folders and a web extension.
                            </IntroText>
                        </HeroSection>

                        <CardGrid>
                            {pages.map((page) => {
                                const IconComponent = FiIcons[page.icon];
                                return (
                                    <MessageCard key={page.path} to={page.path}>
                                        <IconBadge $color={page.color} $background={page.background}>
                                            <IconComponent size={22} />
                                        </IconBadge>
                                        <CardTitle>{page.title}</CardTitle>
                                        <CardBody>{page.description}</CardBody>
                                        <CardCta>
                                            See the page
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </CardCta>
                                    </MessageCard>
                                );
                            })}
                        </CardGrid>

                        <BenefitSection>
                            <SectionTitle>Same habit, purpose-built</SectionTitle>
                            <BenefitGrid>
                                <BenefitItem>
                                    <BenefitHeading>Message yourself everything</BenefitHeading>
                                    <BenefitBody>Capture links, screenshots, photos, voice notes, files, notes and tasks in one chat-style inbox.</BenefitBody>
                                </BenefitItem>
                                <BenefitItem>
                                    <BenefitHeading>Search like you remember</BenefitHeading>
                                    <BenefitBody>Luckynote reads image text, transcribes voice notes and summarizes links, so you can search in plain language.</BenefitBody>
                                </BenefitItem>
                                <BenefitItem>
                                    <BenefitHeading>Follow up without switching tools</BenefitHeading>
                                    <BenefitBody>Set reminders, snooze items and star what matters from the same place you captured it.</BenefitBody>
                                </BenefitItem>
                                <BenefitItem>
                                    <BenefitHeading>Add structure only when you want it</BenefitHeading>
                                    <BenefitBody>Use optional folders, or keep one simple stream and let search do the work.</BenefitBody>
                                </BenefitItem>
                            </BenefitGrid>
                        </BenefitSection>

                        <FooterNote>
                            Want the broader habit? <Link to="/for/people-who-text-themselves">See who texts themselves</Link>.
                            Or browse <Link to="/use-cases/note-to-self">note-to-self</Link> and{' '}
                            <Link to="/use-cases/personal-inbox">personal inbox</Link> workflows.
                        </FooterNote>
                    </PageShell>
                </DarkSection>
            </LayoutDefault>
        </>
    );
};

export default MessageYourselfPage;

const PAPER = '#fcf9f7';
const PAPER_DEEP = '#f6efe7';

const DarkSection = styled.div`
    background:
        radial-gradient(circle at 14% 4%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 26rem),
        linear-gradient(180deg, ${PAPER} 0%, ${PAPER_DEEP} 56%, ${PAPER} 100%);
    min-height: 100vh;
`;

const PageShell = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 96px 20px 96px;
`;

const HeroSection = styled.section`
    padding: 0 0 56px;
    text-align: center;
`;

const HeroTitle = styled.h1`
    margin: 0 0 16px;
    font-size: clamp(2rem, 4vw, 3.2rem);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #14110f;
`;

const HeroDescription = styled.p`
    margin: 0 auto;
    font-size: 1.1rem;
    line-height: 1.7;
    color: rgba(20, 17, 15, 0.66);
    max-width: 42rem;
`;

const IntroText = styled.p`
    margin: 28px auto 0;
    font-size: 1rem;
    line-height: 1.8;
    color: rgba(20, 17, 15, 0.68);
    max-width: 50rem;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
`;

const MessageCard = styled(Link)`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px 26px;
    border-radius: 14px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.58);
    text-decoration: none;
    backdrop-filter: blur(12px);
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.88);
        border-color: rgba(20, 17, 15, 0.22);
        box-shadow: 0 18px 42px rgba(59, 46, 35, 0.16);
        transform: translateY(-3px);
    }
`;

const IconBadge = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $background }) => $background};
    color: ${({ $color }) => $color};
    position: relative;

    img,
    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 1.15rem;
    color: #14110f;
`;

const CardBody = styled.p`
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.65;
    color: rgba(20, 17, 15, 0.62);
    flex-grow: 1;
`;

const CardCta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #2b88d9;

    svg {
        transition: transform 0.15s ease;
    }

    ${MessageCard}:hover & svg {
        transform: translateX(3px);
    }
`;

const BenefitSection = styled.section`
    margin-top: 64px;
`;

const SectionTitle = styled.h2`
    margin: 0 0 24px;
    font-size: clamp(1.6rem, 3vw, 2.2rem);
    color: #14110f;
    text-align: center;
`;

const BenefitGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 18px;
`;

const BenefitItem = styled.div`
    padding: 22px;
    border-radius: 14px;
    border: 1px solid rgba(20, 17, 15, 0.1);
    background: rgba(255, 255, 255, 0.46);
`;

const BenefitHeading = styled.h3`
    margin: 0 0 10px;
    font-size: 1rem;
    color: #14110f;
`;

const BenefitBody = styled.p`
    margin: 0;
    font-size: 0.94rem;
    line-height: 1.65;
    color: rgba(20, 17, 15, 0.62);
`;

const FooterNote = styled.p`
    margin: 48px auto 0;
    text-align: center;
    font-size: 0.95rem;
    color: rgba(20, 17, 15, 0.62);

    a {
        color: #2b88d9;
        font-weight: 600;
    }
`;
